import Chart from "chart.js";
import moment from "moment";
import Modal from '@material-ui/core/Modal';
import React, { Component } from "react";
import Select from 'react-select';
import PropTypes from "prop-types";
import Timeline from "@material-ui/icons/Timeline";
import withStyles from "@material-ui/core/styles/withStyles";
import { forEach, isEqual, isEmpty, keys, map, round, sortBy, values } from "lodash";
import * as Animated from "react-select/lib/animated";
import BouncingLoader from "components/EnergyCompany/BouncingLoader/BouncingLoader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Checkbox from "@material-ui/core/Checkbox";
import ECCard from "components/EnergyCompany/Cards/ECCard.jsx";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import SazoFlex from "./SazoFlex";
import { fulfillWith } from "./utils";
import { getCurvaPerfisAgentes } from "services/companies";
import { localeString } from "services/utils";

const styles = theme => ({
  checkbox: {
    '& svg': {
      margin: 0,
    },
  },
  divider: {
    borderBottom: "1px solid black",
  },
  error: {
    color: 'red',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    height: "80%",
    margin: "auto",
    overflowY: "scroll",
    padding: theme.spacing.unit * 4,
    paddingTop: 0,
    width: "80%",
  },
  modalRoot: {
    display: "flex",
  },
  modalButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
});

class CNAE extends Component {
  state = {
    curvaPerfisAgentesData: {},
    isModalOpened: false,
    empresas: [],
    empresasSelecionadas: [],
    isFetchEmpresasError: false,
    isFetchingEmpresas: false,
    isLoading: true,
    mostrarCurvaTotal: false,
  };

  componentDidMount() {
    const param = { cnaes: [this.props.cnae.id] };
    getCurvaPerfisAgentes(param).then(({ data }) => {
      this.setState({
        curvaPerfisAgentesData: data,
        empresas: data.empresas,
        isLoading: false,
      }, () => {
        this.handleChart();
      });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { mostrarCurvaTotal } = this.state;

    if (!isEqual(prevState.mostrarCurvaTotal, mostrarCurvaTotal)) {
      this.handleChart();
    }
  }

  getLabels(data) {
    let labels = keys(data).map(key => moment(key).format("MMM Y"));
    return fulfillWith(labels, "");
  }

  handleChart(empresasData = []) {
    const { curvaPerfisAgentesData, mostrarCurvaTotal } = this.state;
    const { curvaMedia, curvaTotal } = curvaPerfisAgentesData;
    const ctx = "chart-cnae";
    const data = mostrarCurvaTotal ? curvaTotal : curvaMedia;
    const labels = this.getLabels(data);
    const chartData = map(values(data), 'consumo');
    let empresasDatasets = [];

    if (this.chart) {
      this.chart.destroy();
    }

    forEach(empresasData, empresaData => {
      const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      empresasDatasets.push({
        borderColor: randomColor,
        backgroundColor: randomColor,
        data: map(values(empresaData.data), 'consumo'),
        fill: false,
        label: empresaData.nome,
        type: 'line',
      });
    });

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets: [{
          backgroundColor: 'rgba(255, 99, 132, 0.4)',
          borderColor: 'rgba(255,99,132,1)',
          borderWidth: 1,
          data: chartData,
          label: 'Consumo CNAE',
        },
          ...empresasDatasets,
        ],
        labels,
      },
      options: {
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: value => `${localeString(round(value, 2))} MWh`,
            },
          }],
        },
        tooltips: {
          callbacks: {
            label: tooltipItem => `${localeString(round(tooltipItem.yLabel, 2))} MWh`,
          },
          intersect: false,
          mode: 'index',
        },
      },
    });
  }

  getSazoFlexData() {
    const { curvaPerfisAgentesData } = this.state;

    if (isEmpty(curvaPerfisAgentesData)) {
      return curvaPerfisAgentesData;
    }

    const energiesValues = {};
    forEach(curvaPerfisAgentesData.curvaMedia, (data, month) => {
      energiesValues[month] = {
        consumoMWMedPorPatamar: {
          leve: data.consumoPorPatamar.leve,
          medio: data.consumoPorPatamar.medio,
          pesado: data.consumoPorPatamar.pesado,
          total: data.consumoPorPatamar.total,
          recurso: data.compras + data.geracao,
        },
      };
    });

    return [{ energiesValues, submarket: '--' }];
  }

  fetchEmpresas = () => {
    const { empresasSelecionadas } = this.state;
    this.setState({ isFetchEmpresasError: false, isFetchingEmpresas: true });

    const promises = map(empresasSelecionadas, empresa => {
      const param = { idsEmpresas: [empresa.id] };
      return getCurvaPerfisAgentes(param);
    });

    Promise.all(promises)
      .then(result => {
        const empresasData = map(result, ({ data }) => ({
          nome: data.empresas[0].nome,
          data: data.curvaTotal,
        }));
        this.handleChart(empresasData);
        this.setState({ isModalOpened: false, isFetchingEmpresas: false });
      })
      .catch(() => {
        this.setState({ isFetchEmpresasError: true, isFetchingEmpresas: false });
      });
  }

  render() {
    const { empresas, empresasSelecionadas, isFetchEmpresasError, isFetchingEmpresas, isModalOpened, isLoading, mostrarCurvaTotal } = this.state;
    const { classes } = this.props;
    const sazoFlexData = this.getSazoFlexData();

    return (
      <div>
        <h3 className={classes.divider} key="0">Dados de empresas do ACL com mesma atividade</h3>
        {isLoading ? <BouncingLoader /> : (
          <div>
            <GridContainer key="1">
              <GridItem md={12} sm={12} xs={12}>
                <ECCard
                  actions={
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={mostrarCurvaTotal}
                            className={classes.checkbox}
                            onChange={e => this.setState({ mostrarCurvaTotal: !mostrarCurvaTotal })}
                          />
                        }
                        label="Mostrar curva total"
                      />
                      <Button
                        color="primary"
                        onClick={() => this.setState({ isModalOpened: true })}
                        round
                      >
                        Abrir seleção de empresas
                      </Button>
                    </div>
                  }
                  content={
                    <div>
                      <canvas id="chart-cnae" />
                    </div>
                  }
                  icon={<Timeline />}
                  title="Curva de Consumo Médio do CNAE"
                />
              </GridItem>
            </GridContainer>
            {!isEmpty(sazoFlexData) && (
              <SazoFlex
                checkedAgentProfiles={sazoFlexData}
                key="2"
                title="Perfil de Contratação Média de Mercado"
              />
            )}
          </div>
        )}
        <Modal
          classes={{ root: classes.modalRoot }}
          onClose={() => this.setState({ isModalOpened: false })}
          open={isModalOpened}
        >
          <div className={classes.modalPaper}>
            <h2>Selecionar empresas para comparação</h2>
            <Select
              components={Animated}
              getOptionLabel={option => option.nome}
              getOptionValue={option => option.id}
              isClearable
              isDisabled={false}
              isMulti
              noOptionsMessage={() => 'Selecionar empresas para comparação'}
              onChange={empresas => this.setState({ empresasSelecionadas: empresas })}
              options={sortBy(empresas, ['nome'])}
              placeholder="Empresas"
              value={empresasSelecionadas}
            />
            {isFetchEmpresasError && (
              <h4 className={classes.error}>
                Não conseguimos buscar as empresas, por favor tente mais tarde.
              </h4>
            )}
            <div className={classes.modalButton}>
              <Button
                color="rose"
                onClick={() => this.setState({ isModalOpened: false })}
              >
                Fechar
              </Button>
              {isFetchingEmpresas ? (
                <BouncingLoader />
              ) : (
                <Button
                  color="info"
                  onClick={this.fetchEmpresas}
                >
                  Buscar
                </Button>
              )}
            </div>
          </div>
        </Modal>
      </div>
    );
  }
};

CNAE.propTypes = {
  classes: PropTypes.object.isRequired,
  cnae: PropTypes.shape({
    id: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
  }).isRequired,
};

export { CNAE as CNAEComponent };
export default withStyles(styles)(CNAE);



// WEBPACK FOOTER //
// ./src/views/Dashboard/partials/CNAE.jsx